/**
 * DIE SPALTENLISTEN DER PROJEKT-ABFRAGEN — genau einmal aufgeschrieben.
 *
 * ─── Warum diese Datei existiert ────────────────────────────────────────────
 * `doLoadProjectsFromCloud` und `loadSingleProjectOverview` in
 * `cloudService.ts` fragen dieselben verschachtelten Kindtabellen ab und
 * trugen ihre Spaltenlisten bis hierher DOPPELT im Code. Fast gleichlautend,
 * aber eben nur fast — und genau so wird eine Stelle vergessen: wer eine neue
 * Spalte einbaut, findet die eine Liste und übersieht die andere. Ab jetzt
 * steht jede Liste an einer Stelle, und beide Abfragen lesen daraus.
 *
 * ─── Warum Arrays und keine fertigen Zeichenketten ──────────────────────────
 * Die Listen sind DATEN, nicht Text. Nur so kann die Wache in
 * `scripts/db/vorausgesetzteSpalten.test.ts` maschinell feststellen, welche
 * Datenbankspalten der Client voraussetzt, und sie gegen den in
 * `supabase/MIGRATIONS.md` verzeichneten Migrationsstand halten. Eine Wache,
 * die stattdessen den Quelltext mit einem regulären Ausdruck durchsucht, ist
 * beim nächsten Umbau still falsch.
 *
 * Der Anlass ist ein echter Produktionsausfall: `transferred_from` (Nr. 68)
 * stand in der Liste, das SQL war nicht gelaufen, PostgREST antwortete mit
 * 42703 (undefined_column), der Drei-Versuche-Retry gab auf — und es lud für
 * NIEMANDEN mehr ein Projekt. Derselbe Fall war zuvor schon `min_hours`
 * (Nr. 31) und `quantity_waived` (Nr. 50, auf TEST).
 *
 * ─── Zwei Regeln beim Ändern ────────────────────────────────────────────────
 * 1. Eine Spalte kommt erst hier hinein, wenn ihre Migration auf PROD
 *    verzeichnet ist. Erst das SQL, dann der Client — nie umgekehrt.
 * 2. Legt eine nummerierte Migration die Spalte an, gehört sie zusätzlich in
 *    `SPALTEN_ZUGAENGE` in `scripts/db/kennobjekte.ts`. Ohne diesen Eintrag
 *    hält die Wache sie für Urbestand und schweigt.
 * 3. Ist die Spalte additiv nachgerüstet, gehört sie außerdem in
 *    `ENTBEHRLICHE_SPALTEN` weiter unten — nur was dort steht, darf der
 *    Rückfall aus `spaltenRueckfall.ts` im Notfall weglassen.
 *
 * Whitespace spielt keine Rolle: supabase-js entfernt ihn aus dem
 * `select`-Parameter, bevor die Anfrage rausgeht.
 */

/**
 * Die Spalten von `public.projects` für die PROJEKTLISTE.
 *
 * `metadata:data->metadata` ist Absicht und der einzige Unterschied zur
 * Übersicht unten: die Liste zieht aus dem Blob nur den Metadaten-Zweig, nicht
 * das ganze `data`. Bei über 200 Projekten entscheidet das über die
 * Antwortgröße — die Abfrage ist genau deshalb ohnehin schon seitenweise
 * gebaut (QUIC bricht sonst mitten im Body ab).
 */
export const PROJECT_COLUMNS_LIST = [
    'id',
    'name',
    'updated_at',
    'created_at',
    'metadata:data->metadata',
    'user_id',
    'transfer_target_id',
    'project_number',
    'status',
    'customer_id',
    'next_scheduled_date',
    'last_inspection_date',
    'current_inspection_number',
    'last_exported_inspection_number',
    'is_legacy_data_corrected',
    'address',
    'client_name',
    'coordinator_name',
    'start_date',
    'end_date',
    'category',
    'subcategory',
    // Die Mutter-Tochter-Verknuepfung (Nr. 71). Die Spalten sind die
    // Wahrheit; das gleichnamige Blob-Feld wird beim Laden aus ihnen
    // zusammengesetzt (services/projectLink.ts) und nie mehr geschrieben.
    'parent_project_id',
    'linked_billing_mode',
    'linked_billing_history',
] as const;

/**
 * Dieselben Spalten für die EINZELÜBERSICHT — dort wird `data` vollständig
 * gebraucht (die Übersicht arbeitet auf dem echten Projektzweig weiter).
 */
export const PROJECT_COLUMNS_OVERVIEW = [
    'id',
    'name',
    'updated_at',
    'created_at',
    'data',
    'user_id',
    'transfer_target_id',
    'project_number',
    'status',
    'customer_id',
    'next_scheduled_date',
    'last_inspection_date',
    'current_inspection_number',
    'last_exported_inspection_number',
    'is_legacy_data_corrected',
    'address',
    'client_name',
    'coordinator_name',
    'start_date',
    'end_date',
    'category',
    'subcategory',
    'parent_project_id',
    'linked_billing_mode',
    'linked_billing_history',
] as const;

/**
 * Die Spalten der verschachtelten Leistungspositionen.
 *
 * Diese eine Liste gilt für BEIDE Abfragen — sie war der doppelt geführte
 * Teil, und sie ist zugleich der gefährlichste: die hinteren Einträge
 * stammen aus sieben verschiedenen Migrationen (Nr. 31, 50, 53, 68, 70, 165, 171),
 * und jede einzelne davon legt bei fehlendem SQL die gesamte Projektliste lahm
 * (deshalb stehen sie alle auch in `ENTBEHRLICHE_SPALTEN`).
 */
export const PROJECT_SERVICES_COLUMNS = [
    'id',
    'lv_position',
    'title',
    'quantity_contracted',
    'quantity_used',
    'quantity_billed',
    'unit_price',
    'assignment',
    'unit',
    'is_optional',
    'status',
    'category',
    'client_lv_position',
    'min_hours',
    'quantity_waived',
    'waived_reason',
    'waived_at',
    'waived_by',
    'waived_entry_ids',
    'flat_rate',
    'transferred_from',
    // PAKETPOSITION (Nr. 165, docs/positionsbestandteile-plan.md). Beide
    // Spalten MUESSEN in jedem Ladeweg stehen, der Positionen fuer Geld liest:
    // `bestandteile` ist die Definition (fehlt sie, sieht die Position aus wie
    // eine gewoehnliche und waere ohne Gate abrechenbar), `bestandteile_offen`
    // ist das Gate selbst — von der DATENBANK gepflegt, nur gelesen, nie
    // gesendet (projectLv.bestandteilFelderAusZeile, saveProjectToCloud).
    'bestandteile',
    'bestandteile_offen',
    // STUNDENKONTINGENT der Paketposition (Nr. 171,
    // docs/stundenkontingent-vertrag.md) — rein intern, sperrt nie die
    // Abrechnung. `stunden_kontingent` geht beim Speichern mit, wirkt aber nur
    // beim ANLEGEN der Position (danach haelt der Trigger aus Nr. 172 den alten
    // Wert, S10); `stunden_erfasst` pflegt die DATENBANK — nur gelesen, nie
    // gesendet (projectLv.stundenFelderAusZeile, saveProjectToCloud).
    'stunden_kontingent',
    'stunden_erfasst',
] as const;

/*
 * Die PROJEKTLISTE laedt KEINE Maengel mehr.
 *
 * Bis 2026-09-07 zog sie `inspection_items` ohne `photos` und `closed_image`
 * mit ("die Liste zaehlt und filtert nur"). Beides stimmte nicht mehr: kein
 * Verbraucher der Liste liest `data.items` — weder Dashboard, Warnungen,
 * Aufgaben noch Karte. Und die Zeilen waren nicht nur Ballast, sondern ein
 * Schreibweg fuer Datenverlust: ohne `photos` in der Projektion bekam jeder
 * Mangel `photos: []` statt der Platzhalter, der Riegel in saveProjectToCloud
 * (der auf 'placeholder' prueft) griff nicht, und jedes Speichern eines
 * Listenobjekts (Notiz gelesen im Dashboard, Termin verschoben in der
 * Uebersicht, Datenabgleich in den Einstellungen) schrieb die Bilder aller
 * Maengel des Projekts leer. Ohne die Einbettung bleibt `data.items` an einem
 * Listenobjekt undefined, und `if (items)` am Schreibweg feuert nicht.
 *
 * Die Einzeluebersicht laedt die Maengel weiter (mit Bildern), siehe unten.
 */

/** Die Mängel für die EINZELÜBERSICHT — dort werden die Bilder angezeigt. */
export const INSPECTION_ITEMS_COLUMNS_OVERVIEW = [
    'id',
    'ticket_number',
    'title',
    'description',
    'location',
    'responsible_person',
    'deadline',
    'date_found',
    'status',
    'closed_at',
    'photos',
    'status_comment',
    'closed_image',
] as const;

/**
 * Reduziert einen Listeneintrag auf die Spalte, die dahinter WIRKLICH in der
 * Datenbank stehen muss:
 *
 *   'metadata:data->metadata' → 'data'   (Alias vorne, JSON-Pfad hinten)
 *   'updated_at'              → 'updated_at'
 *
 * Genau diese Spalte ist es, die PostgREST mit 42703 quittiert, wenn sie
 * fehlt — der Alias und der JSON-Pfad sind dem Server egal.
 */
export const baseColumn = (eintrag: string): string => {
    const ohneAlias = eintrag.includes(':') ? eintrag.slice(eintrag.indexOf(':') + 1) : eintrag;
    return ohneAlias.split('->')[0].trim();
};

// ═══════════════════════════════════════════════════════════════════════════
// ENTBEHRLICH — was die Anwendung im Notfall WEGLASSEN darf
// ═══════════════════════════════════════════════════════════════════════════
//
// ─── Wofuer das da ist ──────────────────────────────────────────────────────
// Zwei Schichten verhindern seit dem 02.09.2026 den FEHLER (die CI-Wache
// `scripts/db/vorausgesetzteSpalten.ts` und die Client-Wache
// `src/services/schemaVersion.ts`). Keine verhindert die WIRKUNG, wenn er
// trotzdem passiert — und genau das ist passiert, weil ein Prozess ungefragt
// gepusht und damit deployt hat. Die dritte Schicht ist der Rueckfall in
// `src/services/spaltenRueckfall.ts`: antwortet PostgREST mit 42703, wird die
// benannte Spalte EINMAL aus der Liste genommen und die Abfrage wiederholt.
//
// Diese Liste sagt, welche Spalten dafuer in Frage kommen — und nur diese.
//
// ─── Die Auswahlregel, und warum sie so eng ist ─────────────────────────────
// Entbehrlich ist eine Spalte genau dann, wenn ihr Fehlen die Anwendung
// AERMER, aber nicht FALSCH macht: sie wurde additiv nachgeruestet, jeder
// Lesepfad kennt sie schon als optional (`?? undefined`), und jeder Schreibweg
// laesst sie aus, wenn nichts dazu zu sagen ist. Das trifft auf genau die
// nachgeruesteten Spalten aus `SPALTEN_ZUGAENGE` zu — dieselbe Menge, aus der
// die drei bekannten Ausfaelle kamen (Nr. 31, 50, 68).
//
// NICHT entbehrlich ist der Urbestand (`id`, `lv_position`, `quantity_used`,
// `unit_price`, `data` …). Fehlt eine davon, ist nicht eine Migration
// ausgeblieben, sondern etwas grundlegend falsch — eine fremde Datenbank, ein
// halb gelaufenes Skript, ein zerschossenes Schema. Dann muss es weiterhin
// scheitern: eine Projektliste ohne `quantity_used` zeigte Mengen als 0, und
// die naechste Rechnung forderte alles doppelt. Lieber kein Bild als ein
// falsches.
//
// ─── Beim Ergaenzen ─────────────────────────────────────────────────────────
// `nummer` und `datei` MUESSEN mit `SPALTEN_ZUGAENGE` in
// `scripts/db/kennobjekte.ts` und mit `supabase/MIGRATIONS.md`
// uebereinstimmen; `scripts/db/vorausgesetzteSpalten.test.ts` prueft das. Die
// Angaben stehen hier trotzdem eigens, statt sie zu importieren: `scripts/`
// gehoert nicht ins Browser-Bundle, und die Meldung soll die Migration nennen
// koennen, ohne dass der Client den ganzen Migrationskatalog mitschleppt.

export interface EntbehrlicheSpalte {
    /** schema-qualifiziert, wie in `REQUIRED_COLUMNS.tabelle` */
    tabelle: string;
    spalte: string;
    /** die Migration, die sie anlegt */
    nummer: number;
    /** das Skript unter `supabase/` */
    datei: string;
    /** Was ohne die Spalte fehlt — steht so in der Konsolenmeldung. */
    folge: string;
}

export const ENTBEHRLICHE_SPALTEN: EntbehrlicheSpalte[] = [
    {
        tabelle: 'public.project_services',
        spalte: 'min_hours',
        nummer: 31,
        datei: 'project_services_min_hours.sql',
        folge: 'Mindeststunden je Leistung sind unsichtbar; die Vorbelegung faellt auf 1 zurueck.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'quantity_waived',
        nummer: 50,
        datei: 'service_waiver.sql',
        folge: 'Ein Verzicht ist unsichtbar; die geschlossene Begehung erscheint wieder als offen.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'waived_reason',
        nummer: 50,
        datei: 'service_waiver.sql',
        folge: 'Der Grund des Verzichts ist unsichtbar.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'waived_at',
        nummer: 50,
        datei: 'service_waiver.sql',
        folge: 'Der Zeitpunkt des Verzichts ist unsichtbar.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'waived_by',
        nummer: 50,
        datei: 'service_waiver.sql',
        folge: 'Wer verzichtet hat, ist unsichtbar.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'waived_entry_ids',
        nummer: 70,
        datei: 'project_services_waived_entries.sql',
        folge: 'Welche Begehung der Verzicht meint, ist unsichtbar; es bleibt bei der blossen Menge.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'flat_rate',
        nummer: 53,
        datei: 'monatspauschale.sql',
        folge: 'Die Deckung durch eine Monatspauschale ist unsichtbar; gedeckte Positionen sehen aus wie offenes Geld.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'transferred_from',
        nummer: 68,
        datei: 'project_services_transfer_origin.sql',
        folge: 'Die Herkunft umgehaengter Positionen ist unsichtbar; gleichnamige Positionen an einer Mutter mit mehreren Toechtern sind nicht mehr auseinanderzuhalten.',
    },
    // Nr. 165 — Paketposition. Die beiden Spalten kommen mit derselben
    // Migration und fehlen deshalb im Normalfall gemeinsam. Fehlt nur
    // `bestandteile_offen`, ist das Gate UNBEKANNT und die Paketposition gilt
    // als gesperrt (fail closed, bestandteilLogic.bestandteilGate). Fehlt
    // `bestandteile`, ist die Definition unsichtbar: die Position sieht aus wie
    // eine gewoehnliche. Geschrieben wird dann trotzdem nichts Falsches —
    // `bestandteile` geht nur mit, wenn es gelesen UND geaendert wurde (oder
    // die Position neu ist), `bestandteile_offen` nie (Nachweis in
    // spaltenRueckfall.ts).
    {
        tabelle: 'public.project_services',
        spalte: 'bestandteile',
        nummer: 165,
        datei: 'position_bestandteile.sql',
        folge: 'Paketpositionen sind unsichtbar; ihr Gate fehlt.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'bestandteile_offen',
        nummer: 165,
        datei: 'position_bestandteile.sql',
        folge: 'Gate unbekannt, Paket gilt als gesperrt.',
    },
    // Nr. 171 — Stundenkontingent der Paketposition. Beide Spalten sind rein
    // intern und sperren nie etwas: fehlen sie, sind Kontingent und erfasste
    // Stunden unsichtbar, sonst bleibt alles wie vorher. Geschrieben wird
    // dabei nichts Falsches — `stunden_kontingent` geht nur mit, wenn es
    // gelesen (oder beim Anlegen kopiert) wurde, `stunden_erfasst` nie
    // (Nachweis in spaltenRueckfall.ts). `service_catalog.stunden_kontingent`
    // steht bewusst NICHT hier: der Katalog liest mit `*`, ein 42703 ist dort
    // ausgeschlossen (Muster `service_catalog.bestandteile`).
    {
        tabelle: 'public.project_services',
        spalte: 'stunden_kontingent',
        nummer: 171,
        datei: 'position_stunden.sql',
        folge: 'Stundenkontingent unsichtbar.',
    },
    {
        tabelle: 'public.project_services',
        spalte: 'stunden_erfasst',
        nummer: 171,
        datei: 'position_stunden.sql',
        folge: 'Erfasste Stunden unbekannt.',
    },
    // Nr. 71 — die drei Spalten der Verknuepfung. Fehlen sie, faellt der
    // Client auf das Blob-Feld zurueck (services/projectLink.ts: „Spalte
    // fehlt" ist etwas anderes als „Spalte NULL"), d. h. er verhaelt sich wie
    // vor dem Umbau. Verknuepfen, Trennen und die Abrechnungsart schreiben
    // die Spalten dagegen ausdruecklich und scheitern ohne sie laut.
    {
        tabelle: 'public.projects',
        spalte: 'parent_project_id',
        nummer: 71,
        datei: 'project_link_columns.sql',
        folge: 'Die Mutter-Tochter-Verknuepfung kommt nur noch aus dem ungeschuetzten Blob-Feld; Verknuepfen und Trennen scheitern.',
    },
    {
        tabelle: 'public.projects',
        spalte: 'linked_billing_mode',
        nummer: 71,
        datei: 'project_link_columns.sql',
        folge: 'Die Abrechnungsart der Tochter kommt nur noch aus dem Blob-Feld; Umstellen scheitert.',
    },
    {
        tabelle: 'public.projects',
        spalte: 'linked_billing_history',
        nummer: 71,
        datei: 'project_link_columns.sql',
        folge: 'Die Stichtagshistorie der Abrechnungsart kommt nur noch aus dem Blob-Feld.',
    },
];

/** Der Schluessel, unter dem eine fehlende Spalte gefuehrt wird. */
export const spaltenSchluessel = (tabelle: string, spalte: string): string => `${tabelle}.${spalte}`;

/**
 * Nimmt die als fehlend bekannten Spalten aus einer Liste — sonst NICHTS.
 *
 * Reihenfolge und Schreibweise der uebrigen Eintraege bleiben unveraendert
 * (auch `metadata:data->metadata` behaelt Alias und Pfad): verglichen wird
 * ueber `baseColumn`, geliefert wird der Originaleintrag.
 */
const ohneFehlende = (
    tabelle: string,
    liste: readonly string[],
    fehlend: ReadonlySet<string>,
): string[] => (fehlend.size === 0
    ? [...liste]
    : liste.filter((eintrag) => !fehlend.has(spaltenSchluessel(tabelle, baseColumn(eintrag)))));

/**
 * Baut aus den Listen den `select`-Ausdruck, den PostgREST erwartet.
 *
 * `fehlend` ist im Normalbetrieb leer; gefuellt ist es nur, nachdem eine
 * Abfrage an 42703 gescheitert ist (siehe `spaltenRueckfall.ts`).
 */
const select = (
    projekt: readonly string[],
    maengel: readonly string[] | null,
    fehlend: ReadonlySet<string>,
): string => [
    ohneFehlende('public.projects', projekt, fehlend).join(', '),
    `project_services(${ohneFehlende('public.project_services', PROJECT_SERVICES_COLUMNS, fehlend).join(', ')})`,
    // `null` = keine Maengel einbetten (Projektliste, siehe oben).
    ...(maengel
        ? [`inspection_items(${ohneFehlende('public.inspection_items', maengel, fehlend).join(', ')})`]
        : []),
].join(', ');

const KEINE_FEHLT: ReadonlySet<string> = new Set<string>();

/** Der `select` von `doLoadProjectsFromCloud`. */
export const projektListeSelect = (fehlend: ReadonlySet<string> = KEINE_FEHLT): string =>
    select(PROJECT_COLUMNS_LIST, null, fehlend);

/** Der `select` von `loadSingleProjectOverview`. */
export const projektUebersichtSelect = (fehlend: ReadonlySet<string> = KEINE_FEHLT): string =>
    select(PROJECT_COLUMNS_OVERVIEW, INSPECTION_ITEMS_COLUMNS_OVERVIEW, fehlend);

/** Der vollstaendige `select` der Projektliste — der Normalfall. */
export const PROJECT_LIST_SELECT = projektListeSelect();

/** Der vollstaendige `select` der Einzeluebersicht — der Normalfall. */
export const PROJECT_OVERVIEW_SELECT = projektUebersichtSelect();

/** Eine Tabelle mit den Spalten, die der Client von ihr voraussetzt. */
export interface RequiredColumns {
    /** Schema-qualifiziert, so wie `SPALTEN_ZUGAENGE` in kennobjekte.ts es führt. */
    tabelle: string;
    /** Wo im Client die Voraussetzung entsteht — für die Fehlermeldung. */
    herkunft: string;
    /** Die reinen Spaltennamen, schon durch `baseColumn` gezogen. */
    spalten: string[];
}

/**
 * Was der Client an Datenbankspalten voraussetzt — die Quelle der Wache.
 *
 * Beide Abfragen stehen getrennt drin, obwohl sie sich überschneiden: die
 * Fehlermeldung soll sagen, WELCHE Abfrage stirbt. Wer eine dritte Abfrage mit
 * eigener Spaltenliste baut, hängt sie hier an — sonst prüft die Wache sie nie.
 */
export const REQUIRED_COLUMNS: RequiredColumns[] = [
    {
        tabelle: 'public.projects',
        herkunft: 'cloudService.doLoadProjectsFromCloud (Projektliste)',
        spalten: PROJECT_COLUMNS_LIST.map(baseColumn),
    },
    {
        tabelle: 'public.project_services',
        herkunft: 'cloudService.doLoadProjectsFromCloud (Projektliste)',
        spalten: PROJECT_SERVICES_COLUMNS.map(baseColumn),
    },
    {
        tabelle: 'public.projects',
        herkunft: 'cloudService.loadSingleProjectOverview (Einzelübersicht)',
        spalten: PROJECT_COLUMNS_OVERVIEW.map(baseColumn),
    },
    {
        tabelle: 'public.project_services',
        herkunft: 'cloudService.loadSingleProjectOverview (Einzelübersicht)',
        spalten: PROJECT_SERVICES_COLUMNS.map(baseColumn),
    },
    {
        tabelle: 'public.inspection_items',
        herkunft: 'cloudService.loadSingleProjectOverview (Einzelübersicht)',
        spalten: INSPECTION_ITEMS_COLUMNS_OVERVIEW.map(baseColumn),
    },
];
